import {
  CAMERA_ENABLED_KEY,
  CAMERA_ID_KEY,
  MICROPHONE_ENABLED_KEY,
  MICROPHONE_ID_KEY,
} from 'config/constants';
import { captureException } from 'sentry';
import AgoraRTC from 'agora-rtc-react';

import {
  requestAudioPermissions,
  requestVideoPermissions,
} from 'mic-check/lib/requestMediaPermissions';
import { SensorSelection } from './context-reducer';

function getSelectedCameraOption(
  options: MediaDeviceInfo[],
): MediaDeviceInfo | undefined {
  const selectedCameraId = localStorage.getItem(CAMERA_ID_KEY);
  return options.find((option) => option.deviceId === selectedCameraId);
}

function getSelectedMicrophoneOption(
  options: MediaDeviceInfo[],
): MediaDeviceInfo | undefined {
  const selectedMicrophoneId = localStorage.getItem(MICROPHONE_ID_KEY);
  return options.find((option) => option.deviceId === selectedMicrophoneId);
}

// Default to enabled if nothing is stored in local storage
function getCameraEnabled(): boolean {
  const cameraEnabled = localStorage.getItem(CAMERA_ENABLED_KEY) || 'true';
  return cameraEnabled === 'true';
}

function getMicrophoneEnabled(): boolean {
  const microphoneEnabled =
    localStorage.getItem(MICROPHONE_ENABLED_KEY) || 'true';
  return microphoneEnabled === 'true';
}

export type LoadSensorOptions = {
  loadCamera?: boolean;
  loadMicrophone?: boolean;
};

export async function loadSensorSelection(
  options: LoadSensorOptions = { loadCamera: true, loadMicrophone: true },
): Promise<SensorSelection> {
  const { loadCamera = true, loadMicrophone = true } = options;

  let cameraPermissionError = null;
  let cameraOptions: MediaDeviceInfo[] = [];
  if (loadCamera) {
    try {
      await requestVideoPermissions();
      cameraOptions = await AgoraRTC.getCameras();
    } catch (err: any) {
      captureException(err, { tags: { video: true } });
      cameraPermissionError = err;
    }
  }

  let microphonePermissionError = null;
  let microphoneOptions: MediaDeviceInfo[] = [];
  if (loadMicrophone) {
    try {
      await requestAudioPermissions();
      microphoneOptions = await AgoraRTC.getMicrophones();
    } catch (err: any) {
      captureException(err, { tags: { audio: true } });
      microphonePermissionError = err;
    }
  }

  const defaultCamera = cameraOptions.length ? cameraOptions[0] : undefined;
  const defaultMicrophone = microphoneOptions.length
    ? microphoneOptions[0]
    : undefined;
  return {
    camera: {
      enabled: loadCamera && !cameraPermissionError && getCameraEnabled(),
      error: cameraPermissionError,
      options: cameraOptions,
      selectedOption: getSelectedCameraOption(cameraOptions) || defaultCamera,
    },
    microphone: {
      enabled:
        loadMicrophone && !microphonePermissionError && getMicrophoneEnabled(),
      error: microphonePermissionError,
      options: microphoneOptions,
      selectedOption:
        getSelectedMicrophoneOption(microphoneOptions) || defaultMicrophone,
    },
  };
}
